import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const CONFIRMATION = "DELETE ORDER";

export function DeleteOrderButton({ order, onDeleted, user }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "super_admin") return null;

  const removeOrder = async () => {
    if (typed !== CONFIRMATION) return;
    setBusy(true);
    try {
      await api.delete(`/orders/${order.id}`, { data: { confirm: CONFIRMATION } });
      toast.success(`Order ${order.order_number} removed`);
      setOpen(false);
      setTyped("");
      onDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove this order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md p-2 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-700"
        aria-label={`Remove order ${order.order_number}`}
        title="Remove this order"
        data-testid={`delete-order-trigger-${order.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setTyped("");
      }}>
        <DialogContent data-testid={`delete-order-dialog-${order.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Remove order {order.order_number}
            </DialogTitle>
            <DialogDescription>
              This permanently removes this one website order and its linked payment attempts. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-neutral-700" htmlFor={`delete-order-confirm-${order.id}`}>
              Type <code className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs">{CONFIRMATION}</code> to confirm:
            </label>
            <Input
              id={`delete-order-confirm-${order.id}`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={CONFIRMATION}
              autoFocus
              data-testid={`delete-order-confirm-input-${order.id}`}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid={`delete-order-cancel-${order.id}`}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={removeOrder}
              disabled={typed !== CONFIRMATION || busy}
              data-testid={`delete-order-submit-${order.id}`}
            >
              {busy ? "Removing…" : "Remove order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}